import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { ArrowLeft, Pencil, Plus, Check, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { updateUser, updatePassword, logoutUser } from 'api/authApi';
import { toast } from 'react-hot-toast';
import { updateUserData, logout } from 'store/slices/authSlice';
import { Input } from 'components/ui/input';
import { fileToUrl } from 'api/commonApi';
import Layout from 'components/Layout';
import * as Popover from '@radix-ui/react-popover';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import * as Dialog from '@radix-ui/react-dialog';

const MobileProfileSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  // State for form fields
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  // Edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password1: '',
    new_password2: '',
  });
  // Add these new states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  // State for logout error modal
  const [showLogoutErrorModal, setShowLogoutErrorModal] = useState(false);

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  // Handle display name update
  const handleUpdateDisplayName = async () => {
    try {
      await updateUser({ display_name: displayName });
      dispatch(updateUserData({ displayName }));
      setIsEditingName(false);
      toast.success('Display name updated successfully');
    } catch (error) {
      console.error('Error updating display name:', error);
      toast.error('Failed to update display name');
    }
  };

  // Handle email update
  const handleUpdateEmail = async () => {
    try {
      if (!email) {
        toast.error('Please enter a valid email');
        return;
      }
      await updateUser({ email });
      dispatch(updateUserData({ email }));
      setIsEditingEmail(false);
      toast.success('Email updated successfully');
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update email';
      toast.error(errorMessage);
    }
  };

  // Handle phone update
  const handleUpdatePhone = async () => {
    try {
      if (!phone) {
        toast.error('Please enter a valid phone number');
        return;
      }
      await updateUser({ phone });
      dispatch(updateUserData({ phone }));
      setIsEditingPhone(false);
      toast.success('Phone number updated successfully');
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update phone number';
      toast.error(errorMessage);
    }
  };

  // Handle password update
  const handleUpdatePassword = async () => {
    try {
      if (!passwordForm.old_password) {
        toast.error('Please enter your current password');
        return;
      }
      if (!passwordForm.new_password1) {
        toast.error('Please enter a new password');
        return;
      }
      if (passwordForm.new_password1 !== passwordForm.new_password2) {
        toast.error('New passwords do not match');
        return;
      }
      if (passwordForm.new_password1.length < 8) {
        toast.error('Password must be at least 8 characters long');
        return;
      }

      await updatePassword(passwordForm);
      toast.success('Password updated successfully');
      setPasswordForm({
        old_password: '',
        new_password1: '',
        new_password2: '',
      });
      setIsChangingPassword(false);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || 'Failed to update password';
      toast.error(errorMessage);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const MAX_FILE_SIZE_MB = 0.35;
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        toast.error('File exceeds the 350KB size limit');
        return;
      }

      try {
        const fileUrlResponse = await fileToUrl(file);
        if (!fileUrlResponse.data?.url) {
          throw new Error('Failed to get file URL');
        }
        const updateResponse = await updateUser({
          img_url: fileUrlResponse.data.url,
        });
        dispatch(updateUserData({ imgUrl: updateResponse.data.user.img_url }));
        toast.success('Profile picture updated successfully');
      } catch (error: any) {
        toast.error(error.message || 'Failed to update profile picture');
      }
    }
  };

  const handleRemovePhoto = async () => {
    try {
      await updateUser({ img_url: '' });
      dispatch(updateUserData({ imgUrl: '' }));
      toast.success('Profile picture removed successfully');
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to remove profile picture'
      );
    }
  };

  // Add this new handler
  const handleDeleteAccount = async () => {
    try {
      if (deleteConfirmation !== 'CONFIRM') {
        toast.error('Please type CONFIRM to delete your account');
        return;
      }

      await updateUser({ is_deleted: true });
      dispatch(logout());
      navigate('/auth/login');
      toast.success('Your account has been deleted');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete account';
      toast.error(errorMessage);
    }
  };

  // Improved logout handler with custom error modal
  const handleLogout = async () => {
    try {
      // First call the API while we still have the token
      await logoutUser();
      
      // Then dispatch the logout action to clear Redux state
      dispatch(logout());
      
      // Finally navigate to login page
      navigate('/auth/login');
    } catch (error: any) {
      console.error('Error during logout:', error);
      
      // Show the custom error modal instead of proceeding automatically
      setShowLogoutErrorModal(true);
    }
  };

  // Function to handle forced logout (when user confirms in modal)
  const handleForceLogout = () => {
    dispatch(logout());
    navigate('/auth/login');
    setShowLogoutErrorModal(false);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="flex items-center p-4 border-b border-stroke-primary">
          <button onClick={() => navigate(-1)} className="mr-4">
            <ArrowLeft className="h-6 w-6 text-primary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Profile Picture */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-20 h-20 bg-[#E93D82] rounded-full flex items-center justify-center text-white text-2xl overflow-hidden">
                {user?.imgUrl ? (
                  <img
                    src={user.imgUrl}
                    alt={user.displayName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML =
                        user.displayName?.charAt(0).toUpperCase() || '';
                    }}
                  />
                ) : (
                  <span>{user?.displayName?.charAt(0).toUpperCase()}</span>
                )}
              </div>

              <Popover.Root>
                <Popover.Trigger asChild>
                  <button className="absolute bottom-0 right-0 bg-white rounded-full p-1.5 shadow-md hover:bg-gray-50">
                    <Pencil className="h-4 w-4 text-gray-600" />
                  </button>
                </Popover.Trigger>

                <Popover.Portal>
                  <Popover.Content
                    className="bg-white rounded-md shadow-[0_0_7px_rgba(0,0,0,0.1)] p-0 w-[140px] z-[102]"
                    sideOffset={5}
                  >
                    <div className="flex flex-col">
                      <button
                        onClick={handleRemovePhoto}
                        className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100"
                      >
                        <Trash2 className="h-4 w-4 text-gray-500" />
                        Remove photo
                      </button>
                      <label className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                        <Plus className="h-4 w-4 text-gray-500" />
                        Change photo
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </label>
                    </div>
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-6">
            {/* Username */}
            <div>
              <label className="block text-sm mb-2 text-text-primary">
                Username
              </label>
              <div className="relative flex items-center">
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="flex-1 text-text-primary"
                  disabled={!isEditingName}
                />

                <button
                  className={cn(
                    `ml-2 h-10 px-3 rounded-md bg-background-main hover:bg-background-main/80`,
                    isEditingName ? 'bg-background' : ''
                  )}
                  onClick={() => {
                    if (isEditingName) {
                      handleUpdateDisplayName();
                    } else {
                      setIsEditingName(true);
                    }
                  }}
                >
                  {isEditingName ? (
                    <Check className="h-4 w-4 text-[#7857FF]" />
                  ) : (
                    <Pencil className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm mb-2 text-text-primary">
                Email
              </label>
              <div className="relative flex items-center">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Add email"
                  className="flex-1 text-text-primary"
                  disabled={!isEditingEmail}
                />
                <button
                  className={cn(
                    `ml-2 h-10 px-3 rounded-md bg-background-main hover:bg-background-main/80`,
                    isEditingEmail ? 'bg-background' : ''
                  )}
                  onClick={() => {
                    if (isEditingEmail) {
                      handleUpdateEmail();
                    } else {
                      setIsEditingEmail(true);
                    }
                  }}
                >
                  {isEditingEmail ? (
                    <Check className="h-4 w-4 text-[#7857FF]" />
                  ) : user?.email ? (
                    <Pencil className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Plus className="h-4 w-4 text-[#7857FF]" />
                  )}
                </button>
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm mb-2 text-text-primary">
                Phone number
              </label>
              <div className="relative flex items-center">
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Add phone number"
                  className="flex-1 text-text-primary"
                  disabled={!isEditingPhone}
                />
                <button
                  className={cn(
                    `ml-2 h-10 px-3 rounded-md bg-background-main hover:bg-background-main/80`,
                    isEditingPhone ? 'bg-background' : ''
                  )}
                  onClick={() => {
                    if (isEditingPhone) {
                      handleUpdatePhone();
                    } else {
                      setIsEditingPhone(true);
                    }
                  }}
                >
                  {isEditingPhone ? (
                    <Check className="h-4 w-4 text-[#7857FF]" />
                  ) : user?.phone ? (
                    <Pencil className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Plus className="h-4 w-4 text-[#7857FF]" />
                  )}
                </button>
              </div>
            </div>

            {/* Password */}
            {!isChangingPassword ? (
              <div>
                <label className="block text-sm mb-2 text-text-primary">
                  Password
                </label>
                <div className="relative flex items-center">
                  <Input
                    type="password"
                    value="************"
                    className="flex-1 text-text-primary"
                    disabled
                  />
                  <button
                    className={cn(
                      `ml-2 h-10 px-3 rounded-md bg-background-main hover:bg-background-main/80`,
                      isChangingPassword ? 'bg-background' : ''
                    )}
                    onClick={() => setIsChangingPassword(true)}
                  >
                    <Pencil className="h-4 w-4 text-gray-400" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm mb-2 text-text-primary">
                    Current password
                  </label>
                  <div className="relative flex items-center">
                    <Input
                      type="password"
                      value={passwordForm.old_password}
                      onChange={(e) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          old_password: e.target.value,
                        }))
                      }
                      className="w-full placeholder:text-text-placeholder"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-2 text-text-primary">
                    New password
                  </label>
                  <div className="relative flex items-center">
                    <Input
                      type="password"
                      value={passwordForm.new_password1}
                      onChange={(e) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          new_password1: e.target.value,
                        }))
                      }
                      className="w-full placeholder:text-text-placeholder"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-2 text-text-primary">
                    Confirm new password
                  </label>
                  <div className="relative flex items-center">
                    <Input
                      type="password"
                      value={passwordForm.new_password2}
                      onChange={(e) =>
                        setPasswordForm((prev) => ({
                          ...prev,
                          new_password2: e.target.value,
                        }))
                      }
                      className="w-full placeholder:text-text-placeholder"
                    />
                    <button
                      className={cn(
                        `ml-2 h-10 px-3 rounded-md bg-background-main hover:bg-background-main/80`,
                        isChangingPassword ? 'bg-background' : ''
                      )}
                      onClick={handleUpdatePassword}
                    >
                      <Check className="h-4 w-4 text-[#7857FF]" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Logout */}
          <Button
            variant="outline"
            className="w-full mt-12 border-primary text-primary"
            onClick={handleLogout}
          >
            Logout
          </Button>

          {/* Delete account */}
          <Button
            variant="link"
            className="w-full mt-4 border-primary text-primary underline"
            onClick={() => setIsDeleteDialogOpen(true)}
          >
            Delete account
          </Button>
        </div>

        {/* Add the Delete Account Dialog after the main content div */}
        <Dialog.Root open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[200]" />
            <Dialog.Content className="fixed text-text-primary left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] bg-background rounded-xl w-[400px] p-6 z-[201]">
              <div className="space-y-4">
                <Dialog.Title className="text-lg font-semibold">Delete Account</Dialog.Title>
                <p className="text-sm">
                  This action cannot be undone. This will permanently delete your account and remove all your data.
                </p>
                <p className="text-sm">
                  Please type <span className="font-semibold">CONFIRM</span> to delete your account:
                </p>
                <Input
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder="Type CONFIRM"
                  className="w-full"
                />
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="customSecondary"
                    className="w-full text-sm"
                    onClick={handleDeleteAccount}
                  >
                    Delete Account
                  </Button>
                  <Button 
                    variant="customPrimary"
                    className="w-full text-sm"
                    onClick={() => {
                      setIsDeleteDialogOpen(false);
                      setDeleteConfirmation('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Add the Logout Error Modal */}
        {showLogoutErrorModal && (
          <Dialog.Root open={showLogoutErrorModal} onOpenChange={setShowLogoutErrorModal}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[200]" />
              <Dialog.Content className="fixed text-text-primary left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] bg-background rounded-xl w-[400px] p-6 z-[201]">
                <div className="space-y-4">
                  <Dialog.Title className="text-lg font-semibold">Logout Error</Dialog.Title>
                  <p className="text-sm">
                    Failed to logout on server. Your session may remain active on the server until it expires.
                  </p>
                  <p className="text-sm">
                    Do you still want to log out locally?
                  </p>
                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="customSecondary"
                      className="w-full text-sm"
                      onClick={handleForceLogout}
                    >
                      Log Out Anyway
                    </Button>
                    <Button 
                      variant="customPrimary"
                      className="w-full text-sm"
                      onClick={() => setShowLogoutErrorModal(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )}
      </div>
    </Layout>
  );
};

export default MobileProfileSettings;
